/**
 * Utility functions for Power Reporting components
 * Centralized formatting and helper functions to avoid code duplication
 */

// Cached Intl.NumberFormat instances for performance (js-cache-function-results)
const currencyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const currencyWithSymbolFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/**
 * Format currency with 2 decimal places
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string (without $ symbol)
 */
export const formatCurrency = (amount) => {
  return currencyFormatter.format(amount || 0);
};

/**
 * Format currency with $ symbol for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string with $ symbol
 */
export const formatCurrencyWithSymbol = (amount) => {
  return currencyWithSymbolFormatter.format(amount || 0);
};

/**
 * Format date to MM/DD/YYYY
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date)) return 'Invalid Date';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Format date to YYYY-MM-DD for HTML date input (LOCAL timezone, no conversion)
 * This avoids the timezone shift issue with toISOString() which converts to UTC
 * @param {string|Date} dateValue - Date to format
 * @returns {string} Formatted date string in YYYY-MM-DD format (local time)
 */
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (isNaN(date)) return '';

  // Use local date components to avoid timezone conversion
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Get today's date in YYYY-MM-DD format (LOCAL timezone)
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayForInput = () => {
  return formatDateForInput(new Date());
};

/**
 * Get the date N days ago in YYYY-MM-DD format (LOCAL timezone)
 * @param {number} days - Number of days back from today
 * @returns {string} Date in YYYY-MM-DD format
 */
export const getDateDaysAgoForInput = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatDateForInput(date);
};

/**
 * Get the date N months ago in YYYY-MM-DD format (LOCAL timezone)
 * @param {number} months - Number of months back from today
 * @returns {string} Date in YYYY-MM-DD format
 */
export const getDateMonthsAgoForInput = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return formatDateForInput(date);
};

/**
 * Format date input as user types (MM/DD/YYYY)
 * @param {string} value - Raw date input
 * @returns {string} Formatted date string
 */
export const formatDateInputText = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 8); // MMDDYYYY = 8 digits

  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 4) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  }
};

/**
 * Convert Date object to MM/DD/YYYY display format
 * @param {Date|string} dateValue - Date to format
 * @returns {string} Formatted date string in MM/DD/YYYY format
 */
export const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (isNaN(date)) return '';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Parse date string in MM/DD/YY or MM/DD/YYYY format to Date object
 * Auto-completes 2-digit years (00-25 -> 2000-2025, 26-99 -> 1926-1999)
 * @param {string} dateStr - Date string to parse
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseDateInput = (dateStr) => {
  if (!dateStr) return null;

  // Match MM/DD/YY or MM/DD/YYYY (allowing 1 or 2 digit month/day)
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!match) return null;

  let [, month, day, year] = match;
  month = parseInt(month, 10);
  day = parseInt(day, 10);
  year = parseInt(year, 10);

  // Auto-complete 2-digit years
  if (year < 100) {
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const currentYearLastTwo = currentYear % 100;

    // If 2-digit year <= current year's last 2 digits, use current century
    // Otherwise, use previous century
    year = year <= currentYearLastTwo
      ? currentCentury + year
      : currentCentury - 100 + year;
  }

  // Validate month and day ranges
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day);

  // Check if the date is valid (handles things like Feb 30)
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

/**
 * Format SSN with dashes as user types (XXX-XX-XXXX)
 * @param {string} value - Raw SSN input
 * @returns {string} Formatted SSN
 */
export const formatSSN = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 9);

  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 5) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  } else {
    return `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`;
  }
};

/**
 * Get API credentials from storage and environment (Oath authentication)
 * @returns {Object} Object containing token, baseURL
 */
export const getApiCredentials = () => ({
  token: localStorage.getItem('oathJwtToken'),
  baseURL: process.env.REACT_APP_BASE_URL
});

/**
 * Convert Blob to base64 string
 * Useful for displaying PDFs in viewers that expect base64 data
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} Base64 encoded string (without data URL prefix)
 */
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Format currency input as user types with thousand separators
 * Allows digits and one decimal point, formats with commas
 * @param {string} value - Raw currency input
 * @returns {string} Formatted currency string with commas
 */
export const formatCurrencyInput = (value) => {
  // Allow empty string
  if (!value && value !== 0) return '';

  // Convert to string and remove all non-numeric characters except decimal
  let cleaned = String(value).replace(/[^0-9.]/g, '');

  // Handle multiple decimal points - keep only first
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = cleaned.split('.');

  // Format integer part with commas
  const formattedInteger = integerPart
    ? parseInt(integerPart, 10).toLocaleString('en-US')
    : '';

  // Combine with decimal part if exists
  if (decimalPart !== undefined) {
    // Limit decimal to 2 places
    return `${formattedInteger}.${decimalPart.slice(0, 2)}`;
  }

  return formattedInteger;
};

/**
 * Parse currency input by removing commas and returning number
 * @param {string} value - Formatted currency string
 * @returns {string} Raw numeric string (for form state)
 */
export const parseCurrencyInput = (value) => {
  if (!value && value !== 0) return '';
  return String(value).replace(/,/g, '');
};

/**
 * Parse currency string to number
 * Removes $ symbol, commas and parses to float
 * @param {string|number} value - Formatted currency to parse
 * @returns {number} Numeric value
 */
export const parseCurrency = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number') return value;
  // Remove $ symbol, commas, and whitespace
  const cleaned = String(value).replace(/[$,\s]/g, '');
  return parseFloat(cleaned);
};

/**
 * Get status badge classes based on report status (Tailwind)
 * @param {string} status - Report status
 * @returns {string} Tailwind CSS classes for the badge
 */
export const getStatusBadgeClasses = (status) => {
  const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';

  switch (status?.toUpperCase()) {
    case 'SUBMITTED':
    case 'COMPLETE':
      return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400`;
    case 'PENDING':
    case 'PROCESSING':
      return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400`;
    case 'DRAFT':
      return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    case 'PAID':
      return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
  }
};

/**
 * Get power status badge classes (Tailwind)
 * @param {string} statusCode - Power status code (U, UT, E, T, V, etc.)
 * @returns {Object} Object with bgColor, textColor, and label
 */
export const getPowerStatusBadgeInfo = (statusCode) => {
  const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';

  switch (statusCode?.toUpperCase()) {
    case 'UT':
      return {
        classes: `${baseClasses} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400`,
        label: 'Unexecuted Transfer',
        shortLabel: 'UT'
      };
    case 'U':
      return {
        classes: `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400`,
        label: 'Unexecuted',
        shortLabel: 'U'
      };
    case 'E':
      return {
        classes: `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400`,
        label: 'Executed',
        shortLabel: 'E'
      };
    case 'T':
      return {
        classes: `${baseClasses} bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400`,
        label: 'Transfer Executed',
        shortLabel: 'T'
      };
    case 'V':
      return {
        classes: `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400`,
        label: 'Void',
        shortLabel: 'V'
      };
    case 'F':
      return {
        classes: `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400`,
        label: 'Forfeiture',
        shortLabel: 'F'
      };
    case 'UF':
      return {
        classes: `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400`,
        label: 'Unexecuted Forfeiture',
        shortLabel: 'UF'
      };
    case 'J':
      return {
        classes: `${baseClasses} bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400`,
        label: 'Judgment',
        shortLabel: 'J'
      };
    case 'UJ':
      return {
        classes: `${baseClasses} bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400`,
        label: 'Unexecuted Judgment',
        shortLabel: 'UJ'
      };
    case 'D':
      return {
        classes: `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`,
        label: 'Discharge',
        shortLabel: 'D'
      };
    case 'UD':
      return {
        classes: `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`,
        label: 'Unexecuted Discharge',
        shortLabel: 'UD'
      };
    default:
      return {
        classes: `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`,
        label: statusCode || 'Unknown',
        shortLabel: statusCode || '?'
      };
  }
};

/**
 * Convert string to Title Case (first letter of each word capitalized)
 * Handles ALL CAPS, lowercase, and hyphenated words
 * @param {string} str - String to convert
 * @returns {string} Title-cased string
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/(?:^|\s|-)\w/g, m => m.toUpperCase());
};

// ============================================
// BATCH HELPERS
// ============================================

/**
 * Get the unique batch ID from a batch object.
 * API may return different property names depending on the endpoint.
 * @param {Object} batch - Batch object from API
 * @returns {number} The batch ID
 */
export const getBatchId = (batch) => batch?.reportId || batch?.powerReportId || batch?.batchId || 0;

/**
 * Badge styling + display labels for known batch types.
 * Used by filter chips and inline row badges. Other types returned by the
 * backend are still displayed in tables but don't get a chip or badge.
 */
export const BATCH_TYPE_BADGES = {
  TransferBond: {
    label: 'Transfer Bond',
    color: 'blue',
    bgColor: 'bg-blue-100 dark:bg-blue-900/40',
    textColor: 'text-blue-700 dark:text-blue-300'
  },
  ShippingFee: {
    label: 'Shipping Fee',
    color: 'purple',
    bgColor: 'bg-purple-100 dark:bg-purple-900/40',
    textColor: 'text-purple-700 dark:text-purple-300'
  },
  PowerReport: {
    label: 'Power Report',
    color: 'amber',
    bgColor: 'bg-amber-100 dark:bg-amber-900/40',
    textColor: 'text-amber-700 dark:text-amber-300'
  },
  AttemptToPost: {
    label: 'Attempt to Post',
    color: 'rose',
    bgColor: 'bg-rose-100 dark:bg-rose-900/40',
    textColor: 'text-rose-700 dark:text-rose-300'
  },
  eRecording: {
    label: 'eRecording Fee',
    color: 'emerald',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/40',
    textColor: 'text-emerald-700 dark:text-emerald-300'
  },
  // A batch attached to one of the agency's active payment plans — either the plan's own
  // collection batch (BUF/BSS, carrying the balance as a BRC) or a batch an AR plan was
  // built from. Set server-side by ApplyPaymentPlanClassificationAsync.
  PaymentPlan: {
    label: 'Payment Plan',
    color: 'teal',
    bgColor: 'bg-teal-100 dark:bg-teal-900/40',
    textColor: 'text-teal-700 dark:text-teal-300'
  }
};

/** Display labels by batchType. Derived from BATCH_TYPE_BADGES (single source of truth). */
export const BATCH_TYPE_LABELS = Object.fromEntries(
  Object.entries(BATCH_TYPE_BADGES).map(([type, info]) => [type, info.label])
);

/**
 * Get badge styling for any known batch type.
 * Returns null for unknown types so callers can decide not to render anything.
 */
export const getBatchTypeBadge = (batch) => BATCH_TYPE_BADGES[batch?.batchType] || null;

/**
 * Whether a batch's number should render as a power-inquiry link.
 * TransferBond, ShippingFee, AttemptToPost, and ERecording are operational
 * records that don't have a meaningful power-inquiry view — show their number
 * as flat text instead. (AttemptToPost batches reference an "ATP" marker rather
 * than a real report row, so the inquiry link wouldn't resolve; eRecording
 * batches are invoices, which have no power-inquiry view in the portal.)
 */
export const isLinkableBatch = (batch) => {
  const type = batch?.batchType;
  // A payment plan's own collection batch holds a BRC and no powers, so the inquiry view
  // would come back empty. An AR plan's linked batch is an ordinary power batch that just
  // happens to be attached to a plan, so it stays linkable.
  if (type === 'PaymentPlan') return !batch?.isPlanBalanceBatch;
  return type !== 'TransferBond' && type !== 'ShippingFee' && type !== 'AttemptToPost'
    && type !== 'eRecording';
};

/**
 * Whether a batch is attached to one of the agency's active payment plans.
 *
 * These ARE real batches — a BUF/BSS plan gets a collection batch carrying its balance as
 * a BRC, and an AR plan's own linked batches are relabelled — so they are selectable,
 * payable and totalled like any other batch. Paying one feeds the plan server-side via
 * QueuePaymentPlanTransactionsForPaidBatchesAsync. This flag only drives presentation.
 */
export const isPaymentPlanBatch = (batch) => batch?.batchType === 'PaymentPlan';

/**
 * What is due on a plan's collection batch right now (past due + the current installment,
 * US 4378/4396), capped at the batch's open BUF. Null when it doesn't apply — not a
 * collection batch, no schedule, paid off, or nothing due — so callers fall back to the
 * full amount. Lets Batches to Pay default to this month's payment instead of the whole
 * plan balance, so an agent doesn't pay the plan off by accident.
 */
export const getPlanAmountDue = (batch, plan) => {
  if (!isPaymentPlanBatch(batch) || !batch.isPlanBalanceBatch || !plan) return null;
  if (!plan.hasSchedule || plan.isPaidOff) return null;
  const due = Number(plan.nextDueAmount) || 0;
  const bufDue = nonNegativeAmount(batch.totalBuf ?? batch.openBuf);
  if (due <= 0 || bufDue <= 0) return null;
  return roundCurrency(Math.min(due, bufDue));
};

/**
 * One-line description for a batch row or card.
 *
 * A plan's collection batch has no powers and its associated report number is the internal
 * "buf-payment-plan-…" slug, so plan batches carry the plan's identity instead. Transfer
 * bonds show the defendant; everything else shows the associated report number.
 */
export const describeBatch = (batch) => {
  if (isPaymentPlanBatch(batch)) {
    const name = batch.paymentPlanName || `Plan ${batch.paymentPlanId}`;
    return batch.paymentPlanTypeName ? `${name} · ${batch.paymentPlanTypeName} debt` : name;
  }
  return batch?.batchType === 'TransferBond'
    ? (batch?.defendantName || '-')
    : (batch?.associatedReportNumber || '-');
};

/**
 * Get Transfer Bond type info for display badge.
 * NOTE: TransferBond-only. Used by code that branches on TransferBond specifically
 * (e.g. link-suppression in BatchPaymentModal). For general badge display use
 * `getBatchTypeBadge` instead.
 * @param {Object} batch - Batch object with batchType property
 * @returns {Object|null} Badge styling info or null if not a Transfer Bond
 */
export const getTransferBondInfo = (batch) => {
  if (batch?.batchType === 'TransferBond') {
    return BATCH_TYPE_BADGES.TransferBond;
  }
  return null;
};

/**
 * Round a number to 2 decimal places for currency calculations.
 * Uses banker's rounding (round half to even) to avoid floating point errors.
 * @param {number} value - Value to round
 * @returns {number} Rounded value with at most 2 decimal places
 */
export const roundCurrency = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return 0;
  // Use Math.round with multiplication/division to avoid floating point issues
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

/**
 * Clamp a currency value between 0 and a maximum.
 * Also rounds to 2 decimal places.
 * @param {number|string} value - Value to clamp
 * @param {number} max - Maximum allowed value
 * @returns {number} Clamped and rounded value
 */
export const clampCurrency = (value, max) => {
  const num = roundCurrency(value);
  const maxNum = roundCurrency(max);
  return Math.max(0, Math.min(num, maxNum));
};

/**
 * Treat a currency value as $0 if it's negative (a credit). Credits remain visible
 * on the source row, but they don't reduce or contribute to payment math.
 * Empty/null/undefined/NaN all coerce to 0.
 */
export const nonNegativeAmount = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return 0;
  return num > 0 ? num : 0;
};

/**
 * Sum multiple currency values with proper rounding.
 * @param {...number} values - Values to sum
 * @returns {number} Rounded sum
 */
export const sumCurrency = (...values) => {
  const sum = values.reduce((acc, val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return acc + num;
  }, 0);
  return roundCurrency(sum);
};

/**
 * Normalise a money input value for display on blur — e.g. "17.5" → "17.50".
 * Empty/null/undefined are preserved as '' so the user can clear the field.
 * Non-numeric input is left untouched (rare with type="number" inputs).
 */
export const formatAmountOnBlur = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(2);
};
